import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  IconButton,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useEffect, useState } from 'react';
import { useOrganizationsApi } from 'services/api';

const TEXT_COLOR = '#383838';
const BORDER_DEFAULT = '#DEDEDE';
const CONFIRM_BG = '#9B0000';

export interface RemoveMemberModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  userId: string;
  memberDisplayName?: string;
  onSuccess?: () => Promise<void>;
}

function ModalContent ({
  title,
  isSubmitting,
  onSubmit,
  onClose,
}: {
  title: string;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}): ReactElement {
  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'flex-start',
        pt: '14px',
        px: '20px',
        pb: '36px',
        backgroundColor: 'white',
      }}
    >
      <Box display="flex" justifyContent="flex-end" width="100%">
        <IconButton onClick={onClose} size="small" sx={{ p: 0 }}>
          <CloseIcon sx={{ fontSize: 19, color: TEXT_COLOR }} />
        </IconButton>
      </Box>

      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '16px',
          fontWeight: 500,
          lineHeight: '24px',
          color: TEXT_COLOR,
        }}
      >
        {title}
      </Typography>

      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '21px',
          color: TEXT_COLOR,
        }}
      >
        They will no longer be a member of this organization. They can re-join later by following the organization again.
      </Typography>

      <Box display="flex" gap="10px" alignItems="flex-start" width="100%">
        <Box
          component="button"
          type="button"
          onClick={onClose}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: '13px',
            borderRadius: '10px',
            border: `1px solid ${BORDER_DEFAULT}`,
            backgroundColor: 'white',
            cursor: 'pointer',
          }}
        >
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              color: TEXT_COLOR,
              textDecoration: 'underline',
            }}
          >
            Cancel
          </Typography>
        </Box>

        <Box
          component="button"
          type="submit"
          disabled={isSubmitting}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: '13px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: CONFIRM_BG,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '\'Poppins\', sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
            }}
          >
            Confirm
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export const RemoveMemberModal = ({
  open,
  organizationId,
  userId,
  memberDisplayName,
  onClose,
  onSuccess,
}: RemoveMemberModalProps): ReactElement => {
  const [isSubmitting, setSubmitting] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!open) setSubmitting(false);
  }, [open]);

  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();

  const title = memberDisplayName
    ? `Remove ${memberDisplayName} from the organization?`
    : 'Remove this member from the organization?';

  const onSubmit = throttle(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);

      const result = await organizationsApi.removeOrganizationMember(
        organizationId,
        userId,
      );

      if (result.success) {
        const successLine = memberDisplayName
          ? `${memberDisplayName} removed from organization`
          : 'Member removed successfully';
        enqueueSnackbar(successLine, { variant: 'success' });
        if (onSuccess) await onSuccess();
        return onClose?.();
      }

      setSubmitting(false);
      enqueueSnackbar(result.error.message, { variant: 'error' });
    },
    200,
    { leading: true },
  );

  const content = (
    <ModalContent
      title={title}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => undefined}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: 'hidden',
          },
        }}
      >
        {content}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          maxWidth: 400,
          width: '100%',
          overflow: 'hidden',
        },
      }}
    >
      {content}
    </Dialog>
  );
};
